<template>
  <div class="card">
    <div class="mb-4">
      <h2 class="mt-0 mb-1">Gestione movimenti ignoti</h2>
      <p class="mt-0 text-600">Suggerimenti e assegnazioni dei movimenti senza riferimenti.</p>
    </div>
    <TabView v-model:activeIndex="activeIndex" @tab-change="onTabChange">
      <TabPanel v-if="canSuggest" header="Suggerimenti">
        <div class="flex justify-content-between align-items-center mt-3 mb-3">
          <Button label="Ricarica" icon="pi pi-refresh" class="p-button-rounded p-button-outlined mr-2" :loading="loading" @click="reload" />
          <Button :label="showSuggested ? 'Nascondi già suggeriti' : 'Mostra già suggeriti'" icon="pi pi-filter" class="p-button-rounded p-button-outlined" :loading="loading" @click="toggleSuggested" />
        </div>
        <MovimentiIgnotiList :movimenti-ignoti="movimentiIgnoti" :loading="loading" :can-suggest="true" :can-manage="false" :can-associate="true" :assigning="assigning" :manual-assigning="manualAssigning" :unassigning="unassigning" :managing="managing" :deleting="deleting" @assign="assign" @manual-assign="manualAssign" @manual-manage="manualManage" @unassign="unassign" @manage="manage" @request-delete="openDeleteDialog" />
        <Paginator v-if="totalRecords" class="mt-3" :first="(currentPage - 1) * rowsPerPage" :rows="rowsPerPage" :total-records="totalRecords" :always-show="false" @page="onPageChange" />
      </TabPanel>
      <TabPanel v-if="canManage" header="Assegnazioni">
        <div class="flex justify-content-between align-items-center mt-3 mb-3">
          <Button label="Ricarica" icon="pi pi-refresh" class="p-button-rounded p-button-outlined mr-2" :loading="loading" @click="reload" />
          <Button :label="showAssigned ? 'Nascondi gia associati' : 'Mostra gia associati'" icon="pi pi-filter" class="p-button-rounded p-button-outlined" :loading="loading" @click="toggleAssigned" />
        </div>
        <MovimentiIgnotiList :movimenti-ignoti="movimentiIgnoti" :loading="loading" :can-suggest="false" :can-manage="true" :can-associate="true" :assigning="assigning" :manual-assigning="manualAssigning" :unassigning="unassigning" :managing="managing" :deleting="deleting" @assign="assign" @manual-assign="manualAssign" @manual-manage="manualManage" @unassign="unassign" @manage="manage" @request-delete="openDeleteDialog" />
        <Paginator v-if="totalRecords" class="mt-3" :first="(currentPage - 1) * rowsPerPage" :rows="rowsPerPage" :total-records="totalRecords" :always-show="false" @page="onPageChange" />
      </TabPanel>
    </TabView>
    <Dialog
      v-model:visible="deleteDialogVisible"
      modal
      header="Rimuovi classificazione"
      :style="{ width: '32rem' }"
      :breakpoints="{ '640px': '95vw' }"
      :closable="!deleting"
      :dismissableMask="!deleting"
    >
      <p class="mt-0">
        Stai rimuovendo la classificazione come movimento ignoto dal movimento
        #{{ movementToDelete?.estratto_conto_id }}. Saranno eliminati anche tutti i suggerimenti collegati.
      </p>
      <div class="field mb-0">
        <label for="delete-movement-note" class="block font-medium mb-2">Motivazione *</label>
        <Textarea
          id="delete-movement-note"
          v-model="deleteNote"
          rows="5"
          autoResize
          class="w-full"
          placeholder="Indica perché il movimento non deve essere classificato come ignoto"
          :disabled="deleting !== null"
        />
        <small v-if="deleteSubmitted && !deleteNote.trim()" class="p-error block mt-1">La motivazione è obbligatoria.</small>
      </div>
      <template #footer>
        <Button label="Annulla" class="p-button-text" :disabled="deleting !== null" @click="closeDeleteDialog" />
        <Button label="Rimuovi classificazione" icon="pi pi-trash" class="p-button-danger" :loading="deleting !== null" :disabled="!deleteNote.trim()" @click="deleteMovement" />
      </template>
    </Dialog>
  </div>
</template>

<script>
import { getCurrentInstance, onMounted, ref } from 'vue'
import Service from '@/services/ask-pratiche/pratiche-service'
import MovimentiIgnotiList from '@/components/pratiche/estratticonto/MovimentiIgnotiList.vue'

export default {
  components: { MovimentiIgnotiList },
  setup() {
    const self = getCurrentInstance()
    const service = new Service(self.appContext.config.globalProperties.$http, self.appContext.config.globalProperties.$eventBus)
    const movimentiIgnoti = ref([])
    const loading = ref(false)
    const assigning = ref(null)
    const manualAssigning = ref(null)
    const unassigning = ref(null)
    const managing = ref(null)
    const deleting = ref(null)
    const deleteDialogVisible = ref(false)
    const movementToDelete = ref(null)
    const deleteNote = ref('')
    const deleteSubmitted = ref(false)
    const showSuggested = ref(false)
    const showAssigned = ref(false)
    const canSuggest = ref(false)
    const canManage = ref(false)
    const activeIndex = ref(0)
    const activeView = ref(null)
    const currentPage = ref(1)
    const rowsPerPage = 10
    const totalRecords = ref(0)

    const load = async (view = activeView.value, page = currentPage.value) => {
      movimentiIgnoti.value = []
      loading.value = true
      try {
        const response = await service.getMovimentiIgnotiGestione(view, showSuggested.value, page, showAssigned.value)
        if (!response) return
        canSuggest.value = response.can_suggest === true
        canManage.value = response.can_manage === true
        activeView.value = response.view || view
        movimentiIgnoti.value = response.items || []
        currentPage.value = response.pagination?.page || page
        totalRecords.value = response.pagination?.total || 0
      } finally { loading.value = false }
    }
    const reload = () => load(activeView.value, currentPage.value)
    const toggleAssigned = () => { showAssigned.value = !showAssigned.value; currentPage.value = 1; load('assegnazioni', 1) }
    const toggleSuggested = () => { showSuggested.value = !showSuggested.value; currentPage.value = 1; load('suggerimenti', 1) }
    const onTabChange = ({ index }) => {
      const views = canSuggest.value && canManage.value ? ['suggerimenti', 'assegnazioni'] : [canSuggest.value ? 'suggerimenti' : 'assegnazioni']
      if (views[index]) { currentPage.value = 1; load(views[index], 1) }
    }
    const onPageChange = ({ page }) => { currentPage.value = page + 1; load(activeView.value, currentPage.value) }
    const assign = async (movimentoIgnoto, suggestion) => {
      if (assigning.value) return
      assigning.value = suggestion.id
      try { if (await service.assignMovimentoIgnotoSuggestion(movimentoIgnoto.id, suggestion.id)) await load('suggerimenti') }
      finally { assigning.value = null }
    }
    const manualAssign = async (movimentoIgnoto, invoiceNumbers, isInstallment) => {
      if (manualAssigning.value) return
      manualAssigning.value = movimentoIgnoto.id
      try {
        if (await service.assignManualMovimentoIgnotoSuggestion(movimentoIgnoto.id, invoiceNumbers, isInstallment)) {
          await load('suggerimenti', currentPage.value)
        }
      } finally { manualAssigning.value = null }
    }
    const manualManage = async (movimentoIgnoto, invoiceNumbers, isInstallment) => {
      if (manualAssigning.value || managing.value) return
      manualAssigning.value = movimentoIgnoto.id
      managing.value = movimentoIgnoto.id
      try {
        const suggested = await service.assignManualMovimentoIgnotoSuggestion(movimentoIgnoto.id, invoiceNumbers, isInstallment)
        if (!suggested) return

        await service.associateMovimentoIgnotoPayment(movimentoIgnoto.id)
        await load('suggerimenti', currentPage.value)
      } finally {
        manualAssigning.value = null
        managing.value = null
      }
    }
    const unassign = async (movimentoIgnoto) => {
      if (unassigning.value) return
      unassigning.value = movimentoIgnoto.id
      try {
        if (await service.unassignMovimentoIgnotoSuggestion(movimentoIgnoto.id)) {
          await load(activeView.value, currentPage.value)
        }
      } finally { unassigning.value = null }
    }
    const manage = async (movimentoIgnoto, suggestion = null) => {
      if (managing.value) return
      managing.value = movimentoIgnoto.id
      try {
        if (await service.associateMovimentoIgnotoPayment(movimentoIgnoto.id, suggestion?.id)) {
          await load(activeView.value, currentPage.value)
        }
      }
      finally { managing.value = null }
    }
    const openDeleteDialog = (movimentoIgnoto) => {
      movementToDelete.value = movimentoIgnoto
      deleteNote.value = ''
      deleteSubmitted.value = false
      deleteDialogVisible.value = true
    }
    const closeDeleteDialog = () => {
      if (deleting.value !== null) return
      deleteDialogVisible.value = false
      movementToDelete.value = null
      deleteNote.value = ''
      deleteSubmitted.value = false
    }
    const deleteMovement = async () => {
      deleteSubmitted.value = true
      const note = deleteNote.value.trim()
      if (!movementToDelete.value || !note || deleting.value !== null) return

      deleting.value = movementToDelete.value.id
      try {
        if (await service.deleteMovimentoIgnoto(movementToDelete.value.id, note)) {
          closeDeleteDialogAfterSuccess()
          await load('suggerimenti', currentPage.value)
        }
      } finally { deleting.value = null }
    }
    const closeDeleteDialogAfterSuccess = () => {
      deleteDialogVisible.value = false
      movementToDelete.value = null
      deleteNote.value = ''
      deleteSubmitted.value = false
    }
    onMounted(() => load(null))
    return { movimentiIgnoti, loading, assigning, manualAssigning, unassigning, managing, deleting, deleteDialogVisible, movementToDelete, deleteNote, deleteSubmitted, showSuggested, showAssigned, canSuggest, canManage, activeIndex, currentPage, rowsPerPage, totalRecords, toggleSuggested, toggleAssigned, reload, onTabChange, onPageChange, assign, manualAssign, manualManage, unassign, manage, openDeleteDialog, closeDeleteDialog, deleteMovement }
  }
}
</script>
